import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

// Only a misconfigured PRODUCTION build should ever trip this: development
// legitimately runs on a pk_test_ key. `import.meta.env.PROD` is true only for
// the deployed production bundle, so the banner stays hidden in local dev.
const usingTestKeyInProd =
  import.meta.env.PROD && !!clerkPubKey && clerkPubKey.startsWith("pk_test_");

/**
 * Visible signal that the deployed app is running with a Clerk TEST publishable
 * key (pk_test_). In that state Clerk loads its development instance, shows its
 * own "development keys" warning, and rate-limits auth — so real users start
 * failing to sign in. This banner makes the misconfiguration obvious to whoever
 * opens the deploy (e.g. the admin who shipped it) instead of hiding it in the
 * boot logs. The backend exposes the same status at GET /healthz/config.
 *
 * Copy is intentionally hardcoded (not i18n) since it is an operator-facing
 * diagnostic that only appears on a broken deploy.
 */
export function ClerkConfigBanner() {
  const [dismissed, setDismissed] = useState(false);

  if (!usingTestKeyInProd || dismissed) return null;

  return (
    <div className="fixed top-0 inset-x-0 z-[110] bg-red-950/95 border-b border-red-500/40 backdrop-blur-md">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-red-200">
            Clerk is using TEST keys in production
          </div>
          <div className="text-xs text-red-100/80 mt-0.5">
            This deploy loaded a development publishable key (pk_test_). Sign-in
            is rate-limited and may fail for users. Set a live key (pk_live_) and
            redeploy.
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-red-300 hover:text-red-100 flex-shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
